/**
 * ModuleIntroPanel — renders the inline `<ModuleIntro>` for a module straight
 * from the shared help-content source (CLARID-UX-2026-06).
 *
 * This is the bridge between `helpContent` (PR 1) and the `<ModuleIntro>`
 * primitive: pass a module key, get its intro. PR 3 mounts this on every page;
 * PR 2 uses it on the Compliance Hub and Exam Readiness. Both render targets
 * (inline + the /how-to hub) read the same entry, so they cannot drift.
 */

import { ModuleIntro } from '@/components/ui';
import { helpContent } from '@/lib/help';
import type { ModuleKey } from '@/lib/help';
import GlossaryTooltip from './GlossaryTooltip';

interface ModuleIntroPanelProps {
  moduleKey: ModuleKey;
}

export default function ModuleIntroPanel({ moduleKey }: ModuleIntroPanelProps) {
  const entry = helpContent[moduleKey];
  if (!entry) return null;

  return (
    <ModuleIntro storageKey={moduleKey} title={entry.title}>
      <p>{entry.summary}</p>
      {entry.points.length > 0 && <ModuleIntro.Points items={entry.points} />}
      {entry.glossary.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
          <span className="text-xs font-medium text-gray-500">Key terms:</span>
          {entry.glossary.map((term) => (
            <GlossaryTooltip key={term} termKey={term} />
          ))}
        </div>
      )}
    </ModuleIntro>
  );
}
